/**
 * qrcode-generator 1.4.4, ported to TypeScript.
 *
 * Upstream: https://github.com/kazuhikoarase/qrcode-generator — Copyright (c) 2009 Kazuhiko Arase,
 * licensed MIT (http://www.opensource.org/licenses/mit-license.php). The prototype vendors the
 * minified build as `kaynak/vendor/qrcode.min.js` and `m04-engine.js` calls it through the global
 * `qrcode(0, 'M')`; this file is the same algorithm with types, so the demo can build the matrix
 * without shipping a minified global.
 *
 * Why not the npm `qrcode` package the frontend already depends on: for the seed's thirteen
 * verification URLs it produces the same version and the same data bits, but it picks a different
 * mask — its penalty scorer is the specification's run-length rule, while qrcode-generator scores
 * LEVEL1 by counting equal neighbours in the 3×3 window. Ten of thirteen codes came out with a
 * different mask and roughly 500 of 1369 modules inverted, which a pixel diff sees in full.
 *
 * Word-for-word notes on the quirks that are load-bearing are inline. The tables are lifted
 * verbatim from the vendored build rather than retyped.
 */

export type ErrorCorrectionLevel = 'L' | 'M' | 'Q' | 'H';

/** Upstream's numbering, which is the QR specification's, not the alphabet's. */
const EC_LEVEL: Record<ErrorCorrectionLevel, number> = { L: 1, M: 0, Q: 3, H: 2 };

const MODE_NUMBER = 1 << 0;
const MODE_ALPHA_NUM = 1 << 1;
const MODE_8BIT_BYTE = 1 << 2;
const MODE_KANJI = 1 << 3;

const PATTERN_POSITION_TABLE: readonly (readonly number[])[] = [
  [],
  [6, 18],
  [6, 22],
  [6, 26],
  [6, 30],
  [6, 34],
  [6, 22, 38],
  [6, 24, 42],
  [6, 26, 46],
  [6, 28, 50],
  [6, 30, 54],
  [6, 32, 58],
  [6, 34, 62],
  [6, 26, 46, 66],
  [6, 26, 48, 70],
  [6, 26, 50, 74],
  [6, 30, 54, 78],
  [6, 30, 56, 82],
  [6, 30, 58, 86],
  [6, 34, 62, 90],
  [6, 28, 50, 72, 94],
  [6, 26, 50, 74, 98],
  [6, 30, 54, 78, 102],
  [6, 28, 54, 80, 106],
  [6, 32, 58, 84, 110],
  [6, 30, 58, 86, 114],
  [6, 34, 62, 90, 118],
  [6, 26, 50, 74, 98, 122],
  [6, 30, 54, 78, 102, 126],
  [6, 26, 52, 78, 104, 130],
  [6, 30, 56, 82, 108, 134],
  [6, 34, 60, 86, 112, 138],
  [6, 30, 58, 86, 114, 142],
  [6, 34, 62, 90, 118, 146],
  [6, 30, 54, 78, 102, 126, 150],
  [6, 24, 50, 76, 102, 128, 154],
  [6, 28, 54, 80, 106, 132, 158],
  [6, 32, 58, 84, 110, 136, 162],
  [6, 26, 54, 82, 110, 138, 166],
  [6, 30, 58, 86, 114, 142, 170],
];

const RS_BLOCK_TABLE: readonly (readonly number[])[] = [
  [1, 26, 19], // 1-L
  [1, 26, 16], // 1-M
  [1, 26, 13], // 1-Q
  [1, 26, 9], // 1-H
  [1, 44, 34], // 2-L
  [1, 44, 28], // 2-M
  [1, 44, 22], // 2-Q
  [1, 44, 16], // 2-H
  [1, 70, 55], // 3-L
  [1, 70, 44], // 3-M
  [2, 35, 17], // 3-Q
  [2, 35, 13], // 3-H
  [1, 100, 80], // 4-L
  [2, 50, 32], // 4-M
  [2, 50, 24], // 4-Q
  [4, 25, 9], // 4-H
  [1, 134, 108], // 5-L
  [2, 67, 43], // 5-M
  [2, 33, 15, 2, 34, 16], // 5-Q
  [2, 33, 11, 2, 34, 12], // 5-H
  [2, 86, 68], // 6-L
  [4, 43, 27], // 6-M
  [4, 43, 19], // 6-Q
  [4, 43, 15], // 6-H
  [2, 98, 78], // 7-L
  [4, 49, 31], // 7-M
  [2, 32, 14, 4, 33, 15], // 7-Q
  [4, 39, 13, 1, 40, 14], // 7-H
  [2, 121, 97], // 8-L
  [2, 60, 38, 2, 61, 39], // 8-M
  [4, 40, 18, 2, 41, 19], // 8-Q
  [4, 40, 14, 2, 41, 15], // 8-H
  [2, 146, 116], // 9-L
  [3, 58, 36, 2, 59, 37], // 9-M
  [4, 36, 16, 4, 37, 17], // 9-Q
  [4, 36, 12, 4, 37, 13], // 9-H
  [2, 86, 68, 2, 87, 69], // 10-L
  [4, 69, 43, 1, 70, 44], // 10-M
  [6, 43, 19, 2, 44, 20], // 10-Q
  [6, 43, 15, 2, 44, 16], // 10-H
  [4, 101, 81], // 11-L
  [1, 80, 50, 4, 81, 51], // 11-M
  [4, 50, 22, 4, 51, 23], // 11-Q
  [3, 36, 12, 8, 37, 13], // 11-H
  [2, 116, 92, 2, 117, 93], // 12-L
  [6, 58, 36, 2, 59, 37], // 12-M
  [4, 46, 20, 6, 47, 21], // 12-Q
  [7, 42, 14, 4, 43, 15], // 12-H
  [4, 133, 107], // 13-L
  [8, 59, 37, 1, 60, 38], // 13-M
  [8, 44, 20, 4, 45, 21], // 13-Q
  [12, 33, 11, 4, 34, 12], // 13-H
  [3, 145, 115, 1, 146, 116], // 14-L
  [4, 64, 40, 5, 65, 41], // 14-M
  [11, 36, 16, 5, 37, 17], // 14-Q
  [11, 36, 12, 5, 37, 13], // 14-H
  [5, 109, 87, 1, 110, 88], // 15-L
  [5, 65, 41, 5, 66, 42], // 15-M
  [5, 54, 24, 7, 55, 25], // 15-Q
  [11, 36, 12, 7, 37, 13], // 15-H
  [5, 122, 98, 1, 123, 99], // 16-L
  [7, 73, 45, 3, 74, 46], // 16-M
  [15, 43, 19, 2, 44, 20], // 16-Q
  [3, 45, 15, 13, 46, 16], // 16-H
  [1, 135, 107, 5, 136, 108], // 17-L
  [10, 74, 46, 1, 75, 47], // 17-M
  [1, 50, 22, 15, 51, 23], // 17-Q
  [2, 42, 14, 17, 43, 15], // 17-H
  [5, 150, 120, 1, 151, 121], // 18-L
  [9, 69, 43, 4, 70, 44], // 18-M
  [17, 50, 22, 1, 51, 23], // 18-Q
  [2, 42, 14, 19, 43, 15], // 18-H
  [3, 141, 113, 4, 142, 114], // 19-L
  [3, 70, 44, 11, 71, 45], // 19-M
  [17, 47, 21, 4, 48, 22], // 19-Q
  [9, 39, 13, 16, 40, 14], // 19-H
  [3, 135, 107, 5, 136, 108], // 20-L
  [3, 67, 41, 13, 68, 42], // 20-M
  [15, 54, 24, 5, 55, 25], // 20-Q
  [15, 43, 15, 10, 44, 16], // 20-H
  [4, 144, 116, 4, 145, 117], // 21-L
  [17, 68, 42], // 21-M
  [17, 50, 22, 6, 51, 23], // 21-Q
  [19, 46, 16, 6, 47, 17], // 21-H
  [2, 139, 111, 7, 140, 112], // 22-L
  [17, 74, 46], // 22-M
  [7, 54, 24, 16, 55, 25], // 22-Q
  [34, 37, 13], // 22-H
  [4, 151, 121, 5, 152, 122], // 23-L
  [4, 75, 47, 14, 76, 48], // 23-M
  [11, 54, 24, 14, 55, 25], // 23-Q
  [16, 45, 15, 14, 46, 16], // 23-H
  [6, 147, 117, 4, 148, 118], // 24-L
  [6, 73, 45, 14, 74, 46], // 24-M
  [11, 54, 24, 16, 55, 25], // 24-Q
  [30, 46, 16, 2, 47, 17], // 24-H
  [8, 132, 106, 4, 133, 107], // 25-L
  [8, 75, 47, 13, 76, 48], // 25-M
  [7, 54, 24, 22, 55, 25], // 25-Q
  [22, 45, 15, 13, 46, 16], // 25-H
  [10, 142, 114, 2, 143, 115], // 26-L
  [19, 74, 46, 4, 75, 47], // 26-M
  [28, 50, 22, 6, 51, 23], // 26-Q
  [33, 46, 16, 4, 47, 17], // 26-H
  [8, 152, 122, 4, 153, 123], // 27-L
  [22, 73, 45, 3, 74, 46], // 27-M
  [8, 53, 23, 26, 54, 24], // 27-Q
  [12, 45, 15, 28, 46, 16], // 27-H
  [3, 147, 117, 10, 148, 118], // 28-L
  [3, 73, 45, 23, 74, 46], // 28-M
  [4, 54, 24, 31, 55, 25], // 28-Q
  [11, 45, 15, 31, 46, 16], // 28-H
  [7, 146, 116, 7, 147, 117], // 29-L
  [21, 73, 45, 7, 74, 46], // 29-M
  [1, 53, 23, 37, 54, 24], // 29-Q
  [19, 45, 15, 26, 46, 16], // 29-H
  [5, 145, 115, 10, 146, 116], // 30-L
  [19, 75, 47, 10, 76, 48], // 30-M
  [15, 54, 24, 25, 55, 25], // 30-Q
  [23, 45, 15, 25, 46, 16], // 30-H
  [13, 145, 115, 3, 146, 116], // 31-L
  [2, 74, 46, 29, 75, 47], // 31-M
  [42, 54, 24, 1, 55, 25], // 31-Q
  [23, 45, 15, 28, 46, 16], // 31-H
  [17, 145, 115], // 32-L
  [10, 74, 46, 23, 75, 47], // 32-M
  [10, 54, 24, 35, 55, 25], // 32-Q
  [19, 45, 15, 35, 46, 16], // 32-H
  [17, 145, 115, 1, 146, 116], // 33-L
  [14, 74, 46, 21, 75, 47], // 33-M
  [29, 54, 24, 19, 55, 25], // 33-Q
  [11, 45, 15, 46, 46, 16], // 33-H
  [13, 145, 115, 6, 146, 116], // 34-L
  [14, 74, 46, 23, 75, 47], // 34-M
  [44, 54, 24, 7, 55, 25], // 34-Q
  [59, 46, 16, 1, 47, 17], // 34-H
  [12, 151, 121, 7, 152, 122], // 35-L
  [12, 75, 47, 26, 76, 48], // 35-M
  [39, 54, 24, 14, 55, 25], // 35-Q
  [22, 45, 15, 41, 46, 16], // 35-H
  [6, 151, 121, 14, 152, 122], // 36-L
  [6, 75, 47, 34, 76, 48], // 36-M
  [46, 54, 24, 10, 55, 25], // 36-Q
  [2, 45, 15, 64, 46, 16], // 36-H
  [17, 152, 122, 4, 153, 123], // 37-L
  [29, 74, 46, 14, 75, 47], // 37-M
  [49, 54, 24, 10, 55, 25], // 37-Q
  [24, 45, 15, 46, 46, 16], // 37-H
  [4, 152, 122, 18, 153, 123], // 38-L
  [13, 74, 46, 32, 75, 47], // 38-M
  [48, 54, 24, 14, 55, 25], // 38-Q
  [42, 45, 15, 32, 46, 16], // 38-H
  [20, 147, 117, 4, 148, 118], // 39-L
  [40, 75, 47, 7, 76, 48], // 39-M
  [43, 54, 24, 22, 55, 25], // 39-Q
  [10, 45, 15, 67, 46, 16], // 39-H
  [19, 148, 118, 6, 149, 119], // 40-L
  [18, 75, 47, 31, 76, 48], // 40-M
  [34, 54, 24, 34, 55, 25], // 40-Q
  [20, 45, 15, 61, 46, 16], // 40-H
];

// ------------------------------------------------------------------------- GF(256) (`QRMath`)

const EXP_TABLE: number[] = new Array<number>(256).fill(0);
const LOG_TABLE: number[] = new Array<number>(256).fill(0);
for (let i = 0; i < 8; i += 1) EXP_TABLE[i] = 1 << i;
for (let i = 8; i < 256; i += 1) {
  EXP_TABLE[i] =
    (EXP_TABLE[i - 4] as number) ^
    (EXP_TABLE[i - 5] as number) ^
    (EXP_TABLE[i - 6] as number) ^
    (EXP_TABLE[i - 8] as number);
}
for (let i = 0; i < 255; i += 1) LOG_TABLE[EXP_TABLE[i] as number] = i;

function glog(n: number): number {
  if (n < 1) throw new Error(`glog(${n})`);
  return LOG_TABLE[n] as number;
}

function gexp(n: number): number {
  while (n < 0) n += 255;
  while (n >= 256) n -= 255;
  return EXP_TABLE[n] as number;
}

// ------------------------------------------------------------------- polynomials (`qrPolynomial`)

class Polynomial {
  private readonly num: number[];

  constructor(num: readonly number[], shift: number) {
    let offset = 0;
    while (offset < num.length && num[offset] === 0) offset += 1;
    // Upstream leaves the shift slots as holes and relies on `undefined ^ x === x`; zeros are the
    // same value with a defined type.
    this.num = new Array<number>(num.length - offset + shift).fill(0);
    for (let i = 0; i < num.length - offset; i += 1) this.num[i] = num[i + offset] as number;
  }

  getAt(index: number): number {
    return this.num[index] as number;
  }

  getLength(): number {
    return this.num.length;
  }

  multiply(e: Polynomial): Polynomial {
    const num = new Array<number>(this.getLength() + e.getLength() - 1).fill(0);
    for (let i = 0; i < this.getLength(); i += 1) {
      for (let j = 0; j < e.getLength(); j += 1) {
        num[i + j] = (num[i + j] as number) ^ gexp(glog(this.getAt(i)) + glog(e.getAt(j)));
      }
    }
    return new Polynomial(num, 0);
  }

  mod(e: Polynomial): Polynomial {
    if (this.getLength() - e.getLength() < 0) return this;
    const ratio = glog(this.getAt(0)) - glog(e.getAt(0));
    const num = new Array<number>(this.getLength()).fill(0);
    for (let i = 0; i < this.getLength(); i += 1) num[i] = this.getAt(i);
    for (let i = 0; i < e.getLength(); i += 1) num[i] = (num[i] as number) ^ gexp(glog(e.getAt(i)) + ratio);
    return new Polynomial(num, 0).mod(e);
  }
}

// ------------------------------------------------------------------------ bit buffer, RS blocks

class BitBuffer {
  readonly buffer: number[] = [];
  private length = 0;

  getLengthInBits(): number {
    return this.length;
  }

  putBit(bit: boolean): void {
    const bufIndex = Math.floor(this.length / 8);
    if (this.buffer.length <= bufIndex) this.buffer.push(0);
    if (bit) this.buffer[bufIndex] = (this.buffer[bufIndex] as number) | (0x80 >>> (this.length % 8));
    this.length += 1;
  }

  put(num: number, length: number): void {
    for (let i = 0; i < length; i += 1) this.putBit(((num >>> (length - i - 1)) & 1) === 1);
  }
}

interface RSBlock {
  totalCount: number;
  dataCount: number;
}

function getRSBlocks(typeNumber: number, ecLevel: number): RSBlock[] {
  const offset = ecLevel === EC_LEVEL.L ? 0 : ecLevel === EC_LEVEL.M ? 1 : ecLevel === EC_LEVEL.Q ? 2 : 3;
  const rsBlock = RS_BLOCK_TABLE[(typeNumber - 1) * 4 + offset];
  if (!rsBlock) throw new Error(`bad rs block @ typeNumber:${typeNumber}/errorCorrectionLevel:${ecLevel}`);
  const list: RSBlock[] = [];
  for (let i = 0; i < rsBlock.length / 3; i += 1) {
    const count = rsBlock[i * 3] as number;
    const totalCount = rsBlock[i * 3 + 1] as number;
    const dataCount = rsBlock[i * 3 + 2] as number;
    for (let j = 0; j < count; j += 1) list.push({ totalCount, dataCount });
  }
  return list;
}

// -------------------------------------------------------------------------------------- `QRUtil`

const G15 = (1 << 10) | (1 << 8) | (1 << 5) | (1 << 4) | (1 << 2) | (1 << 1) | (1 << 0);
const G18 = (1 << 12) | (1 << 11) | (1 << 10) | (1 << 9) | (1 << 8) | (1 << 5) | (1 << 2) | (1 << 0);
const G15_MASK = (1 << 14) | (1 << 12) | (1 << 10) | (1 << 4) | (1 << 1);

function getBCHDigit(data: number): number {
  let digit = 0;
  while (data !== 0) {
    digit += 1;
    data >>>= 1;
  }
  return digit;
}

function getBCHTypeInfo(data: number): number {
  let d = data << 10;
  while (getBCHDigit(d) - getBCHDigit(G15) >= 0) d ^= G15 << (getBCHDigit(d) - getBCHDigit(G15));
  return ((data << 10) | d) ^ G15_MASK;
}

function getBCHTypeNumber(data: number): number {
  let d = data << 12;
  while (getBCHDigit(d) - getBCHDigit(G18) >= 0) d ^= G18 << (getBCHDigit(d) - getBCHDigit(G18));
  return (data << 12) | d;
}

function getPatternPosition(typeNumber: number): readonly number[] {
  return PATTERN_POSITION_TABLE[typeNumber - 1] ?? [];
}

function getMaskFunction(maskPattern: number): (i: number, j: number) => boolean {
  switch (maskPattern) {
    case 0:
      return (i, j) => (i + j) % 2 === 0;
    case 1:
      return (i) => i % 2 === 0;
    case 2:
      return (_i, j) => j % 3 === 0;
    case 3:
      return (i, j) => (i + j) % 3 === 0;
    case 4:
      return (i, j) => (Math.floor(i / 2) + Math.floor(j / 3)) % 2 === 0;
    case 5:
      return (i, j) => ((i * j) % 2) + ((i * j) % 3) === 0;
    case 6:
      return (i, j) => (((i * j) % 2) + ((i * j) % 3)) % 2 === 0;
    case 7:
      return (i, j) => (((i * j) % 3) + ((i + j) % 2)) % 2 === 0;
    default:
      throw new Error(`bad maskPattern:${maskPattern}`);
  }
}

function getErrorCorrectPolynomial(errorCorrectLength: number): Polynomial {
  let a = new Polynomial([1], 0);
  for (let i = 0; i < errorCorrectLength; i += 1) a = a.multiply(new Polynomial([1, gexp(i)], 0));
  return a;
}

function getLengthInBits(mode: number, type: number): number {
  if (type >= 1 && type < 10) {
    switch (mode) {
      case MODE_NUMBER:
        return 10;
      case MODE_ALPHA_NUM:
        return 9;
      case MODE_8BIT_BYTE:
        return 8;
      case MODE_KANJI:
        return 8;
    }
  } else if (type < 27) {
    switch (mode) {
      case MODE_NUMBER:
        return 12;
      case MODE_ALPHA_NUM:
        return 11;
      case MODE_8BIT_BYTE:
        return 16;
      case MODE_KANJI:
        return 10;
    }
  } else if (type < 41) {
    switch (mode) {
      case MODE_NUMBER:
        return 14;
      case MODE_ALPHA_NUM:
        return 13;
      case MODE_8BIT_BYTE:
        return 16;
      case MODE_KANJI:
        return 12;
    }
  } else {
    throw new Error(`type:${type}`);
  }
  throw new Error(`mode:${mode}`);
}

/**
 * Upstream's LEVEL1 penalty, which is NOT the specification's run-length rule: it counts how many
 * of the eight neighbours match each module and charges `3 + n - 5` once more than five do. It is
 * reproduced because the mask it elects is what the reference drew.
 */
function getLostPoint(qr: QRCodeMatrix): number {
  const moduleCount = qr.getModuleCount();
  let lostPoint = 0;

  for (let row = 0; row < moduleCount; row += 1) {
    for (let col = 0; col < moduleCount; col += 1) {
      let sameCount = 0;
      const dark = qr.isDark(row, col);
      for (let r = -1; r <= 1; r += 1) {
        if (row + r < 0 || moduleCount <= row + r) continue;
        for (let c = -1; c <= 1; c += 1) {
          if (col + c < 0 || moduleCount <= col + c) continue;
          if (r === 0 && c === 0) continue;
          if (dark === qr.isDark(row + r, col + c)) sameCount += 1;
        }
      }
      if (sameCount > 5) lostPoint += 3 + sameCount - 5;
    }
  }

  for (let row = 0; row < moduleCount - 1; row += 1) {
    for (let col = 0; col < moduleCount - 1; col += 1) {
      let count = 0;
      if (qr.isDark(row, col)) count += 1;
      if (qr.isDark(row + 1, col)) count += 1;
      if (qr.isDark(row, col + 1)) count += 1;
      if (qr.isDark(row + 1, col + 1)) count += 1;
      if (count === 0 || count === 4) lostPoint += 3;
    }
  }

  for (let row = 0; row < moduleCount; row += 1) {
    for (let col = 0; col < moduleCount - 6; col += 1) {
      if (
        qr.isDark(row, col) &&
        !qr.isDark(row, col + 1) &&
        qr.isDark(row, col + 2) &&
        qr.isDark(row, col + 3) &&
        qr.isDark(row, col + 4) &&
        !qr.isDark(row, col + 5) &&
        qr.isDark(row, col + 6)
      ) {
        lostPoint += 40;
      }
    }
  }

  for (let col = 0; col < moduleCount; col += 1) {
    for (let row = 0; row < moduleCount - 6; row += 1) {
      if (
        qr.isDark(row, col) &&
        !qr.isDark(row + 1, col) &&
        qr.isDark(row + 2, col) &&
        qr.isDark(row + 3, col) &&
        qr.isDark(row + 4, col) &&
        !qr.isDark(row + 5, col) &&
        qr.isDark(row + 6, col)
      ) {
        lostPoint += 40;
      }
    }
  }

  let darkCount = 0;
  for (let col = 0; col < moduleCount; col += 1) {
    for (let row = 0; row < moduleCount; row += 1) {
      if (qr.isDark(row, col)) darkCount += 1;
    }
  }

  const ratio = Math.abs((100 * darkCount) / moduleCount / moduleCount - 50) / 5;
  return lostPoint + ratio * 10;
}

/**
 * `qrcode.stringToBytes` — upstream's `default`, which truncates each code unit to one byte. The
 * vendored build never swaps in the `UTF-8` function and neither does the prototype, so a
 * non-ASCII payload would be encoded the same (wrong) way on both surfaces. Left alone on purpose.
 */
function stringToBytes(s: string): number[] {
  const bytes: number[] = [];
  for (let i = 0; i < s.length; i += 1) bytes.push(s.charCodeAt(i) & 0xff);
  return bytes;
}

// ------------------------------------------------------------------------------- the code itself

class QRCodeMatrix {
  private typeNumber: number;
  private readonly ecLevel: number;
  private readonly bytes: number[];
  private moduleCount = 0;
  private modules: (boolean | null)[][] = [];
  private dataCache: number[] | null = null;

  constructor(typeNumber: number, ecLevel: ErrorCorrectionLevel, data: string) {
    this.typeNumber = typeNumber;
    this.ecLevel = EC_LEVEL[ecLevel];
    this.bytes = stringToBytes(data);
  }

  getModuleCount(): number {
    return this.moduleCount;
  }

  isDark(row: number, col: number): boolean {
    const r = this.modules[row];
    if (!r || row < 0 || this.moduleCount <= row || col < 0 || this.moduleCount <= col) {
      throw new Error(`${row},${col}`);
    }
    return r[col] === true;
  }

  make(): void {
    if (this.typeNumber < 1) {
      let typeNumber = 1;
      for (; typeNumber < 40; typeNumber++) {
        const rsBlocks = getRSBlocks(typeNumber, this.ecLevel);
        const buffer = new BitBuffer();
        buffer.put(MODE_8BIT_BYTE, 4);
        buffer.put(this.bytes.length, getLengthInBits(MODE_8BIT_BYTE, typeNumber));
        for (const b of this.bytes) buffer.put(b, 8);
        let totalDataCount = 0;
        for (const block of rsBlocks) totalDataCount += block.dataCount;
        if (buffer.getLengthInBits() <= totalDataCount * 8) break;
      }
      this.typeNumber = typeNumber;
    }
    this.makeImpl(false, this.getBestMaskPattern());
  }

  private getBestMaskPattern(): number {
    let minLostPoint = 0;
    let pattern = 0;
    for (let i = 0; i < 8; i += 1) {
      this.makeImpl(true, i);
      const lostPoint = getLostPoint(this);
      if (i === 0 || minLostPoint > lostPoint) {
        minLostPoint = lostPoint;
        pattern = i;
      }
    }
    return pattern;
  }

  private makeImpl(test: boolean, maskPattern: number): void {
    this.moduleCount = this.typeNumber * 4 + 17;
    this.modules = [];
    for (let row = 0; row < this.moduleCount; row += 1) {
      this.modules.push(new Array<boolean | null>(this.moduleCount).fill(null));
    }
    this.setupPositionProbePattern(0, 0);
    this.setupPositionProbePattern(this.moduleCount - 7, 0);
    this.setupPositionProbePattern(0, this.moduleCount - 7);
    this.setupPositionAdjustPattern();
    this.setupTimingPattern();
    this.setupTypeInfo(test, maskPattern);
    if (this.typeNumber >= 7) this.setupTypeNumber(test);
    if (this.dataCache === null) this.dataCache = this.createData();
    this.mapData(this.dataCache, maskPattern);
  }

  private set(row: number, col: number, value: boolean): void {
    (this.modules[row] as (boolean | null)[])[col] = value;
  }

  private get(row: number, col: number): boolean | null {
    return (this.modules[row] as (boolean | null)[])[col] ?? null;
  }

  private setupPositionProbePattern(row: number, col: number): void {
    for (let r = -1; r <= 7; r += 1) {
      if (row + r <= -1 || this.moduleCount <= row + r) continue;
      for (let c = -1; c <= 7; c += 1) {
        if (col + c <= -1 || this.moduleCount <= col + c) continue;
        this.set(
          row + r,
          col + c,
          (r >= 0 && r <= 6 && (c === 0 || c === 6)) ||
            (c >= 0 && c <= 6 && (r === 0 || r === 6)) ||
            (r >= 2 && r <= 4 && c >= 2 && c <= 4),
        );
      }
    }
  }

  private setupTimingPattern(): void {
    for (let r = 8; r < this.moduleCount - 8; r += 1) {
      if (this.get(r, 6) !== null) continue;
      this.set(r, 6, r % 2 === 0);
    }
    for (let c = 8; c < this.moduleCount - 8; c += 1) {
      if (this.get(6, c) !== null) continue;
      this.set(6, c, c % 2 === 0);
    }
  }

  private setupPositionAdjustPattern(): void {
    const pos = getPatternPosition(this.typeNumber);
    for (let i = 0; i < pos.length; i += 1) {
      for (let j = 0; j < pos.length; j += 1) {
        const row = pos[i] as number;
        const col = pos[j] as number;
        if (this.get(row, col) !== null) continue;
        for (let r = -2; r <= 2; r += 1) {
          for (let c = -2; c <= 2; c += 1) {
            this.set(row + r, col + c, r === -2 || r === 2 || c === -2 || c === 2 || (r === 0 && c === 0));
          }
        }
      }
    }
  }

  private setupTypeNumber(test: boolean): void {
    const bits = getBCHTypeNumber(this.typeNumber);
    for (let i = 0; i < 18; i += 1) {
      const mod = !test && ((bits >> i) & 1) === 1;
      this.set(Math.floor(i / 3), (i % 3) + this.moduleCount - 8 - 3, mod);
    }
    for (let i = 0; i < 18; i += 1) {
      const mod = !test && ((bits >> i) & 1) === 1;
      this.set((i % 3) + this.moduleCount - 8 - 3, Math.floor(i / 3), mod);
    }
  }

  private setupTypeInfo(test: boolean, maskPattern: number): void {
    const data = (this.ecLevel << 3) | maskPattern;
    const bits = getBCHTypeInfo(data);
    for (let i = 0; i < 15; i += 1) {
      const mod = !test && ((bits >> i) & 1) === 1;
      if (i < 6) this.set(i, 8, mod);
      else if (i < 8) this.set(i + 1, 8, mod);
      else this.set(this.moduleCount - 15 + i, 8, mod);
    }
    for (let i = 0; i < 15; i += 1) {
      const mod = !test && ((bits >> i) & 1) === 1;
      if (i < 8) this.set(8, this.moduleCount - i - 1, mod);
      else if (i < 9) this.set(8, 15 - i - 1 + 1, mod);
      else this.set(8, 15 - i - 1, mod);
    }
    this.set(this.moduleCount - 8, 8, !test);
  }

  private mapData(data: readonly number[], maskPattern: number): void {
    let inc = -1;
    let row = this.moduleCount - 1;
    let bitIndex = 7;
    let byteIndex = 0;
    const maskFunc = getMaskFunction(maskPattern);
    for (let col = this.moduleCount - 1; col > 0; col -= 2) {
      if (col === 6) col -= 1;
      for (;;) {
        for (let c = 0; c < 2; c += 1) {
          if (this.get(row, col - c) === null) {
            let dark = false;
            if (byteIndex < data.length) dark = (((data[byteIndex] as number) >>> bitIndex) & 1) === 1;
            if (maskFunc(row, col - c)) dark = !dark;
            this.set(row, col - c, dark);
            bitIndex -= 1;
            if (bitIndex === -1) {
              byteIndex += 1;
              bitIndex = 7;
            }
          }
        }
        row += inc;
        if (row < 0 || this.moduleCount <= row) {
          row -= inc;
          inc = -inc;
          break;
        }
      }
    }
  }

  private createData(): number[] {
    const rsBlocks = getRSBlocks(this.typeNumber, this.ecLevel);
    const buffer = new BitBuffer();
    buffer.put(MODE_8BIT_BYTE, 4);
    buffer.put(this.bytes.length, getLengthInBits(MODE_8BIT_BYTE, this.typeNumber));
    for (const b of this.bytes) buffer.put(b, 8);

    let totalDataCount = 0;
    for (const block of rsBlocks) totalDataCount += block.dataCount;
    if (buffer.getLengthInBits() > totalDataCount * 8) {
      throw new Error(`code length overflow. (${buffer.getLengthInBits()}>${totalDataCount * 8})`);
    }
    if (buffer.getLengthInBits() + 4 <= totalDataCount * 8) buffer.put(0, 4);
    while (buffer.getLengthInBits() % 8 !== 0) buffer.putBit(false);
    for (;;) {
      if (buffer.getLengthInBits() >= totalDataCount * 8) break;
      buffer.put(0xec, 8);
      if (buffer.getLengthInBits() >= totalDataCount * 8) break;
      buffer.put(0x11, 8);
    }
    return createBytes(buffer, rsBlocks);
  }
}

function createBytes(buffer: BitBuffer, rsBlocks: readonly RSBlock[]): number[] {
  let offset = 0;
  let maxDcCount = 0;
  let maxEcCount = 0;
  const dcdata: number[][] = [];
  const ecdata: number[][] = [];

  for (let r = 0; r < rsBlocks.length; r += 1) {
    const dcCount = (rsBlocks[r] as RSBlock).dataCount;
    const ecCount = (rsBlocks[r] as RSBlock).totalCount - dcCount;
    maxDcCount = Math.max(maxDcCount, dcCount);
    maxEcCount = Math.max(maxEcCount, ecCount);

    const dc = new Array<number>(dcCount).fill(0);
    for (let i = 0; i < dc.length; i += 1) dc[i] = 0xff & (buffer.buffer[i + offset] as number);
    offset += dcCount;
    dcdata.push(dc);

    const rsPoly = getErrorCorrectPolynomial(ecCount);
    const modPoly = new Polynomial(dc, rsPoly.getLength() - 1).mod(rsPoly);
    const ec = new Array<number>(rsPoly.getLength() - 1).fill(0);
    for (let i = 0; i < ec.length; i += 1) {
      const modIndex = i + modPoly.getLength() - ec.length;
      ec[i] = modIndex >= 0 ? modPoly.getAt(modIndex) : 0;
    }
    ecdata.push(ec);
  }

  let totalCodeCount = 0;
  for (const block of rsBlocks) totalCodeCount += block.totalCount;
  const data = new Array<number>(totalCodeCount).fill(0);
  let index = 0;
  for (let i = 0; i < maxDcCount; i += 1) {
    for (let r = 0; r < rsBlocks.length; r += 1) {
      const dc = dcdata[r] as number[];
      if (i < dc.length) {
        data[index] = dc[i] as number;
        index += 1;
      }
    }
  }
  for (let i = 0; i < maxEcCount; i += 1) {
    for (let r = 0; r < rsBlocks.length; r += 1) {
      const ec = ecdata[r] as number[];
      if (i < ec.length) {
        data[index] = ec[i] as number;
        index += 1;
      }
    }
  }
  return data;
}

/**
 * `qrcode(0, level).addData(text).make()` followed by the engine's own row/column read-out —
 * `m04-engine.js` `qrMatrix`. Returns `null` the way the reference does when the code cannot be
 * built, so the caller draws its dashed placeholder instead of throwing.
 */
export function qrMatrix(text: string, errorCorrection: ErrorCorrectionLevel = 'M'): boolean[][] | null {
  try {
    const qr = new QRCodeMatrix(0, errorCorrection, String(text));
    qr.make();
    const n = qr.getModuleCount();
    const rows: boolean[][] = [];
    for (let r = 0; r < n; r++) {
      const row: boolean[] = [];
      for (let c = 0; c < n; c++) row.push(qr.isDark(r, c));
      rows.push(row);
    }
    return rows;
  } catch {
    return null;
  }
}
